import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { FieldErrors, Resolver, SubmitErrorHandler } from 'react-hook-form'
import type { AxiosError } from 'axios'
import { Building2, ChevronDown } from 'lucide-react'
import { Bone, Button, Card } from '@/components/ui'
import { PageShell } from '@/components/common/PageShell'
import { cn } from '@/lib/cn'
import { useCurrentOrganization } from '@/features/organization/hooks/useCurrentOrganization'
import { useUpdateOrganization } from '@/features/organization/hooks/useUpdateOrganization'
import { usePermissions } from '@/hooks/usePermissions'
import {
  organizationFormSchema,
  type OrganizationFormValues,
} from '@/features/organization/schemas/organizationForm.schema'
import {
  DEFAULT_AGENT,
  DEFAULT_ATTENDANCE,
  DEFAULT_BREAK,
  DEFAULT_CHAT,
  DEFAULT_LEAVE,
  DEFAULT_NOTIFICATION,
  DEFAULT_PAYROLL,
  DEFAULT_SHIFT_ALERT,
  formValuesFromOrganization,
  formValuesToPayload,
} from '@/features/organization/utils/organizationFormUtils'
import {
  availableSections,
  DEFAULT_SECTION_ID,
  ERROR_FIELD_ORDER,
  resolveActiveSection,
  SECTIONS,
  type SectionId,
} from '@/features/organization/constants/sections'
import { useCustomDomainsAvailable } from '@/features/organization/hooks/useCustomDomainsAvailable'
import { OrganizationHeader } from './OrganizationHeader'
import { SaveBar, SectionRail } from './organization-ui'
import { AddressPane } from './panes/AddressPane'
import { AgentPane } from './panes/AgentPane'
import { AttendancePane } from './panes/AttendancePane'
import { BrandingPane } from './panes/BrandingPane'
import { DomainPane } from './panes/DomainPane'
import { BreakPane } from './panes/BreakPane'
import { CategoriesPane } from './panes/CategoriesPane'
import { ChatPane } from './panes/ChatPane'
import { GeneralPane } from './panes/GeneralPane'
import { IdentityPane } from './panes/IdentityPane'
import { LeavePane } from './panes/LeavePane'
import { NotificationsPane } from './panes/NotificationsPane'
import { PayrollPane } from './panes/PayrollPane'
import { ShiftAlertsPane } from './panes/ShiftAlertsPane'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import { QueryErrorState } from '@/components/common/QueryErrorState'

const ORG_BREADCRUMB = [{ label: 'Organization' }] as const
const ORG_DESCRIPTION = "Configure your organization's settings, branding, and policies."

function countDirtyLeaves(value: unknown): number {
  if (value === true) return 1
  if (value == null || typeof value !== 'object') return 0
  return Object.values(value).reduce<number>((sum, child) => sum + countDirtyLeaves(child), 0)
}

function getPathValue(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (current == null || typeof current !== 'object') return undefined
    return (current as Record<string, unknown>)[segment]
  }, source)
}

function sectionForField(field: string): SectionId {
  const root = field.split('.')[0]
  return SECTIONS.find((section) => section.fields.includes(root))?.id ?? DEFAULT_SECTION_ID
}

function firstErrorField(errors: FieldErrors<OrganizationFormValues>): string | null {
  return ERROR_FIELD_ORDER.find((field) => Boolean(getPathValue(errors, field))) ?? null
}

/** Page frame shared by the permission, loading, error, and empty states. */
function OrganizationFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <PageShell
      breadcrumbItems={[...ORG_BREADCRUMB]}
      icon={Building2}
      title="Organization"
      description={ORG_DESCRIPTION}
      className="gap-0"
      hideHeader
    >
      {children}
    </PageShell>
  )
}

export function OrganizationPage(): React.ReactElement {
  const { data: organization, isLoading, error, refetch } = useCurrentOrganization()
  const { can } = usePermissions()
  const canRead = can('settings', 'read')
  const canUpdate = can('settings', 'update')

  const [selectedSection, setSelectedSection] = useState<SectionId>(DEFAULT_SECTION_ID)
  const [query, setQuery] = useState('')
  const [railOpen, setRailOpen] = useState(false)
  const [validationMessage, setValidationMessage] = useState<string | null>(null)
  const mutation = useUpdateOrganization()
  const navigate = useNavigate()

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, dirtyFields },
    reset,
    setValue,
    setFocus,
  } = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema) as Resolver<OrganizationFormValues>,
    defaultValues: {
      name: '',
      slug: '',
      registrationNumber: '',
      taxId: '',
      industry: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
      street: '',
      city: '',
      state: '',
      country: '',
      postalCode: '',
      primaryColor: '',
      footerText: '',
      termsUrl: '',
      privacyUrl: '',
      status: 'active',
      defaultCurrency: '',
      supportedCurrencies: [],
      timezone: '',
      dateFormat: '',
      companyCodePrefix: 'CO',
      workWeek: [],
      attendanceSettings: DEFAULT_ATTENDANCE,
      breakSettings: DEFAULT_BREAK,
      agentSettings: DEFAULT_AGENT,
      shiftAlertSettings: DEFAULT_SHIFT_ALERT,
      payrollSettings: DEFAULT_PAYROLL,
      leaveSettings: DEFAULT_LEAVE,
      notificationSettings: DEFAULT_NOTIFICATION,
      chatSettings: DEFAULT_CHAT,
    },
  })

  const primaryColorValue = useWatch({ control, name: 'primaryColor' })
  const watchedName = useWatch({ control, name: 'name' })
  const watchedStatus = useWatch({ control, name: 'status' })

  const savedValues = useMemo(
    () => (organization ? formValuesFromOrganization(organization) : null),
    [organization]
  )

  useUnsavedChangesGuard(isDirty && canUpdate)

  useEffect(() => {
    if (organization && !isDirty) {
      reset(formValuesFromOrganization(organization))
    }
  }, [isDirty, organization, reset])

  const customDomains = useCustomDomainsAvailable()
  const sections = useMemo(() => availableSections({ customDomains }), [customDomains])

  const filteredSections = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return sections
    return sections.filter(
      (section) =>
        section.label.toLowerCase().includes(normalized) || section.keywords.includes(normalized)
    )
  }, [query, sections])

  // Resolve during render so the rail and body never disagree: a hidden section
  // opens the default one, and when a filter hides the selected pane, fall
  // through to the first match rather than syncing state.
  const activeSection: SectionId = resolveActiveSection(selectedSection, sections, filteredSections)

  const sectionStatus = useMemo(
    () =>
      SECTIONS.reduce(
        (acc, section) => {
          acc[section.id] = {
            dirty: section.fields.some((field) => countDirtyLeaves(getPathValue(dirtyFields, field)) > 0),
            error: section.fields.some((field) => Boolean(getPathValue(errors, field))),
          }
          return acc
        },
        {} as Record<SectionId, { dirty: boolean; error: boolean }>
      ),
    [dirtyFields, errors]
  )

  const handleSelectSection = useCallback((id: SectionId) => {
    setSelectedSection(id)
    setRailOpen(false)
  }, [])

  if (!canRead) {
    return (
      <OrganizationFrame>
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            You don&apos;t have permission to view organization settings.
          </p>
          <Button variant="secondary" type="button" className="mt-3" onClick={() => navigate('/dashboard')}>
            Go to dashboard
          </Button>
        </Card>
      </OrganizationFrame>
    )
  }

  if (isLoading) {
    return (
      <OrganizationFrame>
        <div role="status" aria-busy="true" aria-label="Loading organization" className="kc-loader-in space-y-4">
          <Bone className="h-10 w-64 rounded-lg" />
          <Bone className="h-[28rem] rounded-2xl" />
        </div>
      </OrganizationFrame>
    )
  }

  if (error) {
    return (
      <PageShell
        breadcrumbItems={[...ORG_BREADCRUMB]}
        icon={Building2}
        title="Organization"
        description={ORG_DESCRIPTION}
        className="gap-0"
        hideHeader
      >
        <QueryErrorState
          error={error}
          title="Couldn't load your organization"
          onRetry={refetch}
        />
      </PageShell>
    )
  }

  if (!organization) {
    return (
      <OrganizationFrame>
        <Card>
          <p className="text-sm text-gray-600 dark:text-gray-400">No organization data available.</p>
        </Card>
      </OrganizationFrame>
    )
  }

  const onSubmit = (values: OrganizationFormValues): void => {
    setValidationMessage(null)
    mutation.mutate(formValuesToPayload(values), {
      onSuccess: (updatedOrganization) => {
        reset(formValuesFromOrganization(updatedOrganization))
      },
    })
  }

  const onInvalid: SubmitErrorHandler<OrganizationFormValues> = (submitErrors): void => {
    const field = firstErrorField(submitErrors)
    setValidationMessage('Fix the highlighted fields before saving.')
    if (!field) return
    setQuery('')
    setSelectedSection(sectionForField(field))
    window.setTimeout(() => {
      setFocus(field as Parameters<typeof setFocus>[0])
      document
        .querySelector<HTMLElement>(`[name="${field}"], #${field}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 80)
  }

  const handleDiscard = (): void => {
    if (!savedValues) return
    setValidationMessage(null)
    mutation.reset()
    reset(savedValues)
  }

  const apiError =
    mutation.error == null
      ? null
      : mutation.error instanceof Error
        ? mutation.error.message
        : (mutation.error as unknown as AxiosError<{ message?: string }>)?.response?.data?.message ??
          null

  const formProps = { register, setValue, watch, errors, disabled: !canUpdate }
  const dirtyCount = countDirtyLeaves(dirtyFields)
  const activeMeta = SECTIONS.find((section) => section.id === activeSection)

  const renderPane = (): React.ReactElement => {
    switch (activeSection) {
      case 'identity':
        return (
          <IdentityPane
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            disabled={!canUpdate}
          />
        )
      case 'address':
        return <AddressPane register={register} errors={errors} disabled={!canUpdate} />
      case 'branding':
        return (
          <BrandingPane
            primaryColorValue={primaryColorValue}
            register={register}
            setValue={setValue}
            errors={errors}
            disabled={!canUpdate}
          />
        )
      case 'domain':
        return <DomainPane disabled={!canUpdate} />
      case 'general':
        return <GeneralPane {...formProps} />
      case 'attendance':
        return <AttendancePane {...formProps} />
      case 'break':
        return <BreakPane {...formProps} />
      case 'agent':
        return <AgentPane {...formProps} />
      case 'categories':
        return <CategoriesPane {...formProps} />
      case 'shift-alerts':
        return <ShiftAlertsPane {...formProps} />
      case 'payroll':
        return <PayrollPane {...formProps} />
      case 'leave':
        return <LeavePane {...formProps} />
      case 'notifications':
        return <NotificationsPane {...formProps} />
      case 'chat':
        return <ChatPane {...formProps} />
    }
  }

  return (
    <OrganizationFrame>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        <OrganizationHeader
          name={watchedName || organization.name}
          status={watchedStatus}
          subscription={organization.subscription}
          seatCount={organization.seats?.count}
          primaryColor={primaryColorValue}
          canUpdate={canUpdate}
        />

        <Card className="[overflow:clip]" contentClassName="p-0">
          {/* Mobile: the rail collapses behind a disclosure naming the current pane. */}
          <div className="border-b border-gray-200 p-3 lg:hidden dark:border-gray-700">
            <button
              type="button"
              onClick={() => setRailOpen((open) => !open)}
              aria-expanded={railOpen}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:hover:bg-gray-700/50"
            >
              <span className="min-w-0">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Section
                </span>
                <span className="mt-0.5 block truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {activeMeta?.label ?? 'Settings'}
                </span>
              </span>
              <ChevronDown
                className={cn(
                  'h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200',
                  railOpen && 'rotate-180'
                )}
                strokeWidth={2}
                aria-hidden
              />
            </button>
            {railOpen && (
              <div className="mt-3">
                <SectionRail
                  sections={filteredSections}
                  activeId={activeSection}
                  onSelect={handleSelectSection}
                  status={sectionStatus}
                  query={query}
                  onQueryChange={setQuery}
                />
              </div>
            )}
          </div>

          <div className="lg:grid lg:grid-cols-[15rem_minmax(0,1fr)]">
            {/*
              The card uses `overflow: clip` rather than `hidden`: clip still
              trims the rounded corners but does NOT create a scroll container,
              so the rail below can stick to the page scroll.
            */}
            <div className="hidden border-r border-gray-200 p-4 lg:block dark:border-gray-700">
              <div className="sticky top-6 flex max-h-[calc(100vh-10rem)] flex-col">
                <SectionRail
                  sections={filteredSections}
                  activeId={activeSection}
                  onSelect={handleSelectSection}
                  status={sectionStatus}
                  query={query}
                  onQueryChange={setQuery}
                />
              </div>
            </div>

            <div className="min-w-0 p-5 sm:p-6">{renderPane()}</div>
          </div>

          <SaveBar
            apiError={apiError}
            validationMessage={validationMessage}
            dirtyCount={dirtyCount}
            hasValidationErrors={Object.keys(errors).length > 0}
            isPending={mutation.isPending}
            canUpdate={canUpdate}
            onDiscard={handleDiscard}
          />
        </Card>
      </form>
    </OrganizationFrame>
  )
}

OrganizationPage.displayName = 'OrganizationPage'
